import PropTypes from 'prop-types';
import React from 'react';

import classNames from 'classnames';

import { supportsPassiveEvents } from 'detect-passive-events';
import spring from 'react-motion/lib/spring';
import Overlay from 'react-overlays/Overlay';

import { text2emotional } from '../../../utils/emotional';
import Motion from '../../ui/util/optional_motion';

import TextIconButton from './text_icon_button';

const listenerOptions = supportsPassiveEvents ? { passive: true } : false;

class EmotionalDropdownMenu extends React.PureComponent {

  static propTypes = {
    style: PropTypes.object,
    items: PropTypes.array.isRequired,
    value: PropTypes.string.isRequired,
    placement: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
  };

  state = {
    mounted: false,
  };

  handleDocumentClick = e => {
    if (this.node && !this.node.contains(e.target) && this.state.mounted) {
      this.props.onClose();
      e.stopPropagation();
    }
  }

  handleKeyDown = e => {
    const { items } = this.props;
    const value = e.currentTarget.getAttribute('data-index');
    const index = items.findIndex(item => {
      return (item.value === value);
    });
    let element = null;

    switch(e.key) {
    case 'Escape':
      this.props.onClose();
      break;
    case 'Enter':
      this.handleClick(e);
      break;
    case 'ArrowDown':
      element = this.node.childNodes[index + 1] || this.node.firstChild;
      break;
    case 'ArrowUp':
      element = this.node.childNodes[index - 1] || this.node.lastChild;
      break;
    case 'Tab':
      if (e.shiftKey) {
        element = this.node.childNodes[index - 1] || this.node.lastChild;
      } else {
        element = this.node.childNodes[index + 1] || this.node.firstChild;
      }
      break;
    case 'Home':
      element = this.node.firstChild;
      break;
    case 'End':
      element = this.node.lastChild;
      break;
    }

    if (element) {
      element.focus();
      this.props.onSelect(element.getAttribute('data-index'));
      e.preventDefault();
      e.stopPropagation();
    }
  }

  handleClick = e => {
    const value = e.currentTarget.getAttribute('data-index');

    e.preventDefault();

    this.props.onClose();
    this.props.onSelect(value);
  }

  componentDidMount () {
    document.addEventListener('click', this.handleDocumentClick, false);
    document.addEventListener('touchend', this.handleDocumentClick, listenerOptions);
    if (this.focusedItem) this.focusedItem.focus({ preventScroll: true });
    requestAnimationFrame(() => this.setState({ mounted: true }));
  }

  componentWillUnmount () {
    document.removeEventListener('click', this.handleDocumentClick, false);
    document.removeEventListener('touchend', this.handleDocumentClick, listenerOptions);
  }

  setRef = c => {
    this.node = c;
  }

  setFocusRef = c => {
    this.focusedItem = c;
  }

  render () {
    const { mounted } = this.state;
    const { style, items, placement, value } = this.props;

    return (
      <Motion defaultStyle={{ opacity: 0, scaleX: 0.85, scaleY: 0.75 }} style={{ opacity: spring(1, { damping: 35, stiffness: 400 }), scaleX: spring(1, { damping: 35, stiffness: 400 }), scaleY: spring(1, { damping: 35, stiffness: 400 }) }}>
        {({ opacity, scaleX, scaleY }) => (
          // It should not be transformed when mounting because the resulting
          // size will be used to determine the coordinate of the menu by
          // react-overlays
          <div className={`emotional-dropdown__dropdown privacy-dropdown__dropdown emoji-mart-scroll ${placement}`} style={{ ...style, opacity: opacity, transform: mounted ? `scale(${scaleX}, ${scaleY})` : null, paddingTop: 6 }} role='listbox' ref={this.setRef}>
            {items.map(item => (
              <div role='option' tabIndex='0' key={item.value} data-index={item.value} onKeyDown={this.handleKeyDown} onClick={this.handleClick} className={classNames('privacy-dropdown__option', { active: item.value === value })} aria-selected={item.value === value} ref={item.value === value ? this.setFocusRef : null} style={{ borderRadius: 4 }}>
                <div className='privacy-dropdown__option__content'>
                  <strong>{item.text}</strong>
                  {item.meta}
                </div>
              </div>
            ))}
          </div>
        )}
      </Motion>
    );
  }

}

export default class EmotionalDropdown extends React.PureComponent {

  static propTypes = {
    isUserTouching: PropTypes.func,
    onModalOpen: PropTypes.func,
    onModalClose: PropTypes.func,
    value: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
    noDirect: PropTypes.bool,
    container: PropTypes.func,
  };

  state = {
    open: false,
    placement: 'bottom',
  };

  handleToggle = ({ target }) => {
    if (this.props.isUserTouching && this.props.isUserTouching()) {
      if (this.state.open) {
        this.props.onModalClose();
      } else {
        this.props.onModalOpen({
          actions: this.options.map(option => ({ ...option, active: option.value === this.props.value })),
          onClick: this.handleModalActionClick,
        });
      }
    } else {
      const { top } = target.getBoundingClientRect();
      if (this.state.open && this.activeElement) {
        this.activeElement.focus({ preventScroll: true });
      }
      this.setState({ placement: top * 2 < innerHeight ? 'bottom' : 'top' });
      this.setState({ open: !this.state.open });
    }
  }

  handleModalActionClick = (e) => {
    e.preventDefault();

    const { value } = this.options[e.currentTarget.getAttribute('data-index')];

    this.props.onModalClose();
    this.props.onSelect(value);
  }

  handleKeyDown = e => {
    switch(e.key) {
    case 'Escape':
      this.handleClose();
      break;
    }
  }

  handleMouseDown = () => {
    if (!this.state.open) {
      this.activeElement = document.activeElement;
    }
  }

  handleButtonKeyDown = (e) => {
    switch(e.key) {
    case ' ':
    case 'Enter':
      this.handleMouseDown();
      break;
    }
  }

  handleClose = () => {
    if (this.state.open && this.activeElement) {
      this.activeElement.focus({ preventScroll: true });
    }
    this.setState({ open: false });
  }

  handleSelect = value => {
    this.props.onSelect(value);
  }

  setTargetRef = c => {
    this.target = c;
  }

  findTarget = () => {
    return this.target;
  }

  componentWillMount () {
    const previewText = 'Lorem ipsum dolor sit amet,';

    this.options = [
      { value: 'boldSerif', text: 'Serif (bold)', meta: text2emotional(previewText, 'boldSerif') },
      { value: 'italicSerif', text: 'Serif (italic)', meta: text2emotional(previewText, 'italicSerif') },
      { value: 'boldItalicSerif', text: 'Serif (bold italic)', meta: text2emotional(previewText, 'boldItalicSerif') },
      { value: 'boldSansSerif', text: 'Sans-serif (bold)', meta: text2emotional(previewText, 'boldSansSerif') },
      { value: 'italicSansSerif', text: 'Sans-serif (italic)', meta: text2emotional(previewText, 'italicSansSerif') },
      { value: 'boldItalicSansSerif', text: 'Sans-serif (bold italic)', meta: text2emotional(previewText, 'boldItalicSansSerif') },
      { value: 'boldScript', text: 'Script (bold)', meta: text2emotional(previewText, 'boldScript') },
      { value: 'fraktur', text: 'Fraktur', meta: text2emotional(previewText, 'fraktur') },
      { value: 'frakturBold', text: 'Fraktur (bold)', meta: text2emotional(previewText, 'frakturBold') },
      { value: 'monoSpace', text: 'Monospace', meta: text2emotional(previewText, 'monoSpace') },
    ];
  }

  render () {
    const { value, container } = this.props;
    const { open, placement } = this.state;

    return (
      <div className={classNames('privacy-dropdown', placement, { active: open })} onKeyDown={this.handleKeyDown}>
        <div className={classNames('privacy-dropdown__value')} ref={this.setTargetRef}>
          <TextIconButton
            className='privacy-dropdown__value-icon'
            label={'𝓐𝓪'}
            expanded={open}
            active={open}
            inverted
            onClick={this.handleToggle}
            onMouseDown={this.handleMouseDown}
            onKeyDown={this.handleButtonKeyDown}
          />
        </div>

        <Overlay show={open} placement={placement} target={this.findTarget} container={container} popperConfig={{ strategy: 'fixed' }}>
          {({ props, placement })=> (
            <div {...props} style={{ ...props.style, opacity: 1, pointerEvents: 'auto' }}>
              <div className={`dropdown-animation ${placement}`}>
                <EmotionalDropdownMenu
                  items={this.options}
                  value={value || ""}
                  onClose={this.handleClose}
                  onSelect={this.handleSelect}
                  placement={placement}
                />
              </div>
            </div>
          )}
        </Overlay>
      </div>
    );
  }

}
